import { Component, inject } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { FormControlBase } from "src/app/common-components/form-controls/form-control-base";
import { IFormDropDownItem } from "src/app/common-components/form-controls/form-dropdown/dropdown.component";
import { CstPdfViewerPopupComponent } from "src/app/common-components/file-viewers/pdf-viewer/popup/pdf-viewer-popup.component";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  TaskFormularCreateCommand,
  FormularVorlagenClientProvider,
  TasksClientProvider,
  FormulareClientProvider,
  FormularDetailDto,
  XmlFormularGeneratePreviewCommand,
  ValidationResult,
  IFormularVorlageReportLookupDto,
  IFormularVorlageLookupDto,
  ITaskFormularCreateCommand,
} from "src/app/web-api/web-api";
import {
  CstFormBuilder,
  CstFormGroup,
} from "src/app/_uncategorised_models_services/cst-form-group";

export interface ITaskFormularCreateData {
  taskID: string;
  anlageNr: string;
}

@Component({
    templateUrl: "./task-formular-create.component.html",
    styleUrls: ["./task-formular-create.component.scss"],
    standalone: false
})
export class TaskFormularCreateComponent extends CstResponsivePopupComponentBase<ITaskFormularCreateData, FormularDetailDto> {
  private vorlagenClient = inject(FormularVorlagenClientProvider);
  private taskClient = inject(TasksClientProvider);
  private formularClient = inject(FormulareClientProvider);
  private _popupService = inject(CstResponsivePopupService);

  private _selectedVorlage: IFormDropDownItem;

  public form: CstFormGroup<ITaskFormularCreateCommand> = null;
  public pdfSrc: Blob = null;
  public pdfSrcLoading = false;
  public vorlagen: IFormDropDownItem[] = [];
  public reports: IFormularVorlageReportLookupDto[] = [];
  public currentReportIdx = 0;
  public isSaving = false;

  constructor() {
    const fb = inject(CstFormBuilder);

    super();
    this.form = fb.group<ITaskFormularCreateCommand>({
      taskID: new FormControl("", Validators.required),
      anlageNr: new FormControl(""),
      vorlageID: new FormControl("", Validators.required),
      beschreibung: new FormControl(""),
    });
  }

  onInitialised() {
    if (this.data) {
      this.form.patchValue(this.data);
    }
  }

  public getLabelOfCurrentReportOrDefault() {
    return this.reports.length > 0
      ? this.reports[this.currentReportIdx].bezeichnung
      : $localize`:@@PlanungAPI_FormularCreate_Preview:Preview`;
  }

  public canAccept(): boolean {
    return !this.isSaving && this.form.valid;
  }

  public onAccept(): void {
    if (!this.canAccept()) {
      return;
    }
    this.isSaving = true;
    const cmd = new TaskFormularCreateCommand(this.form.value);
    this.taskClient
      .createFormular(cmd)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(
        (result) => {
          this.close(result);
        },
        (error) => {
          if (error instanceof ValidationResult) {
            FormControlBase.ApplyValidationResultToForm(this.form, error);
            return;
          }
          console.log(error);
        }
      );
  }

  public onCancel() {
    this.close();
  }

  public onVorlageChanged(item: IFormDropDownItem) {
    this._selectedVorlage = item;
    this.currentReportIdx = 0;
    if (!item || !item.value) {
      this.pdfSrc = null;
      this.reports = [];
      return;
    }
    this.reports = (
      this._selectedVorlage.data as IFormularVorlageLookupDto
    ).reports;

    this._refreshPdfSource();
  }

  public onPreviewBefore() {
    if (--this.currentReportIdx < 0) {
      this.currentReportIdx = this.reports.length - 1;
    }
    this._refreshPdfSource();
  }
  public onPreviewNext() {
    if (++this.currentReportIdx >= this.reports.length) {
      this.currentReportIdx = 0;
    }
    this._refreshPdfSource();
  }
  public onPreviewPopup() {
    if (!this.pdfSrc || this.pdfSrcLoading) {
      return;
    }
    this._popupService.open(CstPdfViewerPopupComponent, {
      data: { src: this.pdfSrc, title: this._selectedVorlage.label },
      maxHeight: "96vh",
    });
  }

  private _refreshPdfSource() {
    this.pdfSrcLoading = true;
    const cmd = new XmlFormularGeneratePreviewCommand();
    cmd.init({
      vorlageID: this._selectedVorlage.value,
      reportID:
        this.reports.length > 0 ? this.reports[this.currentReportIdx].id : null,
      previewData: { fillDummyData: true },
    });
    this.formularClient
      .generatePreview(cmd)
      .pipe(finalize(() => (this.pdfSrcLoading = false)))
      .subscribe((result) => {
        this.pdfSrc = result.data;
      });
  }
}
